import React, { useCallback, useEffect, useState } from 'react';

import { Radio, RadioGroup } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';

import config from '../../config';

import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { format } from 'date-fns';


import { Badge } from '../../catalyst/badge';
import { Button } from '../../catalyst/button';
import { Dialog, DialogBody, DialogTitle } from '../../catalyst/dialog';
import { Heading } from '../../catalyst/heading';
import LoadingMain from '../../components/LoadingMain';

const stripePromise = loadStripe(config.stripePublicKey);



const frequencies = [
  { value: 'month', label: 'Monthly', priceSuffix: '/month' },
  { value: 'annually', label: 'Annually', priceSuffix: '/year' },
]
const tiers = [
  {
    name: 'One month (30 days)',
    id: 'tier-30days',
    href: '#',
    price: '$3.99',
    description: 'The essentials to transfer the playlists and try sync',
    features: ['Transfer unlimited tracks/albums', 'Search and add missing items', 'No monthly subsciption', '48-hour support response time'],
    featured: false,
    cta: 'Choose plan',
  },
  {
    name: 'Monthly',
    id: 'tier-monthly',
    href: '#',
    price: { monthly: '$2.99', annually: '$29' },
    description: 'A plan that scales for your hobby',
    features: ['Transfer unlimited tracks/albums', 'Search and add missing items', '48-hour support response time'
    ],
    featured: false,
    cta: 'Choose plan',
  },
  {
    name: 'Lifetime',
    id: 'tier-lifetime',
    href: '#',
    price: '$29.99',
    description: 'Pay once and control your music forever',
    features: [ 'Access to premium feature for lifetime', 'Transfer unlimited tracks/albums', 'Search and add missing items', '48-hour support response time'
    ],
    featured: true,
    cta: 'Choose plan',
  },
]

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}



export default function PremiumOptions() {

  const [frequency, setFrequency] = useState(frequencies[0])



  const [openMonth, setMonthOpen] = React.useState(false);
  const handleMonthOpen = () => setMonthOpen(true);
  const handleMonthClose = () => setMonthOpen(false);

  const [openLifetime, setLifetimeOpen] = React.useState(false);
  const handleLifetimeOpen = () => setLifetimeOpen(true);
  const handleLifetimeClose = () => setLifetimeOpen(false);

  const [loading, setLoading] = useState(true); // State to track loading state
  const [tier, setTier] = useState(''); // Tier can be 'Free' or 'Premium'
  const [premiumExpiresOn, setPremiumExpiresOn] = useState('')


  const params = new URLSearchParams(window.location.search);
  const [redirectMessage, setRedirectMessage] = useState(params.get('message'))


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true); // Set loading state to true
        const response = await fetch(config.apiUrl + "/user", {
          headers: {
            'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('user')).accessToken,
          }
        });

        const status = await response.status;
        if (status === 200) {
          let data = await response.json()
          setTier(data.premium ? 'Premium' : 'Free')
          if (data.premium) {
            setPremiumExpiresOn(data.payment.expiryDate)
          }
        } else {
          throw new Error('Failed to get profile');
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false)
      } finally {
        setLoading(false); // Set loading state to false after fetch completes

      }
    };

    fetchData();
  }, []);



  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };


  function CheckoutForm(stripePriceType) {
    const fetchClientSecret = useCallback(() => {
      // Create a Checkout Session
      return fetch(config.apiUrl + "/stripe/createSession?stripePriceType=" + stripePriceType, {
        method: "GET",
        headers: {
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('user')).accessToken,
          'Content-Type': 'application/json'
        },
      })
        .then((res) => {
          return res.json()
        })
        .then((data) => data.stripeSession.clientSecret)
        .catch((error) => {
          console.error('Error:', error);
        });
    }, []);

    const options = { fetchClientSecret };

    return (
      <div id="checkout">
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={options}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    )
  }


  function handlePremiumOpen(selected) {
    if(selected === "tier-30days") {
      handleMonthOpen()
    } else if(selected === "tier-monthly") {
      handleMonthOpen()
    } else if (selected === "tier-lifetime") {
      handleLifetimeOpen()
    }
    
  }

  function premiumSubscriptionNotice(endDate) {
    const formattedDate = format(new Date(endDate), 'MMMM do, yyyy, h:mm a');
    return (
      <div className="flex items-center gap-x-6 bg-gray-900 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
      <p className="text-sm/6 text-white">
        
          Premium Subscription Active. Your premium subscription is active until {formattedDate}.
      </p>
    </div>
    )
  }

  function featuresView(disablePurchase) {
    return (<>
        <div className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <p className="mt-2 text-5xl font-semibold tracking-tight text-balance text-gray-900 sm:text-6xl">
            Pricing that grows with you
          </p>
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-lg font-medium text-pretty text-gray-600 sm:text-xl/8">
          Choose an affordable plan that’s packed with the best options for you
        </p>
        <div className="mt-16 flex justify-center">
          <fieldset aria-label="Payment frequency">
            <RadioGroup
              value={frequency}
              onChange={setFrequency}
              className="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs/5 font-semibold ring-1 ring-gray-200 ring-inset"
            >
              {frequencies.map((option) => (
                <Radio
                  key={option.value}
                  value={option}
                  className="cursor-pointer rounded-full px-2.5 py-1 text-gray-500 data-checked:bg-zinc-900 data-checked:text-white"
                >
                  {option.label}
                </Radio>
              ))}
            </RadioGroup>
          </fieldset>
        </div>
        <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={classNames(
                tier.featured ? 'bg-gray-900 ring-gray-900' : 'ring-gray-200',
                'rounded-3xl p-8 ring-1 xl:p-10',
              )}
            >
              <h3
                id={tier.id}
                className={classNames(tier.featured ? 'text-white' : 'text-gray-900', 'text-lg/8 font-semibold')}
              >
                {tier.name}
              </h3>
              <p className={classNames(tier.featured ? 'text-gray-300' : 'text-gray-600', 'mt-4 text-sm/6')}>
                {tier.description}
              </p>
              <p className="mt-6 flex items-baseline gap-x-1">
                <span
                  className={classNames(
                    tier.featured ? 'text-white' : 'text-gray-900',
                    'text-4xl font-semibold tracking-tight',
                  )}
                >
                  {typeof tier.price === 'string' ? tier.price : tier.price[frequency.value]}
                </span>
                {typeof tier.price !== 'string' ? (
                  <span
                    className={classNames(tier.featured ? 'text-gray-300' : 'text-gray-600', 'text-sm/6 font-semibold')}
                  >
                    {frequency.priceSuffix}
                  </span>
                ) : null}
              </p>
              <Button
                onClick={() => handlePremiumOpen(tier.id)}
                href="#"
                aria-describedby={tier.id}
                className='w-full my-5 bg-zinc-900 text-gray-500'
                disabled={disablePurchase}
              >
                {tier.cta}
              </Button>
              <ul
                role="list"
                className={classNames(
                  tier.featured ? 'text-gray-300' : 'text-gray-600',
                  'mt-8 space-y-3 text-sm/6 xl:mt-10',
                )}
              >
                {tier.features.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <CheckIcon
                      aria-hidden="true"
                      className={classNames(tier.featured ? 'text-white' : 'text-zinc-900', 'h-6 w-5 flex-none')}
                    />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>


        <Dialog
          open={openMonth}
          onClose={handleMonthClose}
        >
            <DialogTitle>Buy Monthly</DialogTitle>

          <DialogBody>
            {CheckoutForm("MONTH")}
          </DialogBody>
        </Dialog>

        <Dialog
          open={openLifetime}
          onClose={handleLifetimeClose}
        >
            <DialogTitle>Buy Lifetime</DialogTitle>

          <DialogBody>
            {CheckoutForm("LIFETIME")}
          </DialogBody>
        </Dialog>
    
    </>
    )
  }

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-sm:w-full sm:flex-1">
          <Heading>Premium options</Heading>
          <div className="mt-5">
            {redirectMessage === "PAYMENT_NOT_COMPLETE" ? <Badge color="rose">Payment not complete. Please try again or contact us.</Badge> : <></>}
            {redirectMessage === "PAYMENT_COMPLETE" ? <Badge color="lime" >Thank you. You are now a premium user.</Badge> : <></>}




            {loading ? <>
              <LoadingMain />
              {featuresView(true)}
            </> :

              tier === 'Free' ?
                <>
                  {featuresView(false)}
                </>
                :
                <>
                  {premiumSubscriptionNotice(premiumExpiresOn)}
                  {featuresView(true)}
                </>
            }


          </div>
        </div>

      </div>
    </>
  )
}